import React, { useState, useEffect, useRef, useMemo } from 'react';
import * as XLSX from 'xlsx-js-style';
import { DataGrid } from '@mui/x-data-grid';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { Box, Chip, Tooltip, IconButton, Card, CardContent, Typography, Paper, Button, TextField, FormControl, InputLabel, Select, MenuItem, Checkbox, ListItemIcon, ListItemText } from '@mui/material';
import { Edit as EditIcon, Download as DownloadIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { hitosFacturacionAPI, iniciativasAPI } from '../services/api';
import HitoModal from '../components/HitoModal';
import './TablaHitosFacturacion.css';

const TablaHitosFacturacion = () => {
  const [todosLosHitos, setTodosLosHitos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);


  // Estados de filtros
  const [filtros, setFiltros] = useState({
    mesFiltro: [],           // [] = todos, ['__NONE__'] = ninguno
    añoFiltro: [],           // [] = todos, ['__NONE__'] = ninguno
    iniciativasFiltro: [],   // [] = todos, ['__NONE__'] = ninguno
    estadoFiltro: [],        // [] = todos
    clientesFiltro: [],      // [] = todos
    sociedadesFiltro: [],    // [] = todos
    searchText: ''
  });

  // Opciones para los dropdowns
  const [meses, setMeses] = useState([]);
  const [años, setAños] = useState([]);
  const [iniciativas, setIniciativas] = useState([]);
  const [clientes, setClientes] = useState([]);
  const [sociedades, setSociedades] = useState([]);

  // Estado para el modal
  const [modalOpen, setModalOpen] = useState(false);
  const [hitoSeleccionado, setHitoSeleccionado] = useState(null);

  // Estado para visibilidad de columnas
  const [columnVisibilityModel, setColumnVisibilityModel] = useState(() => {
    const saved = localStorage.getItem('hitosFacturacion_columnVisibility');
    return saved ? JSON.parse(saved) : {};
  });

  const gridRef = useRef(null);

  // Tema personalizado
  const theme = createTheme({
    palette: {
      primary: {
        main: '#0d6efd',
      },
      secondary: {
        main: '#6c757d',
      },
    },
    components: {
      MuiDataGrid: {
        styleOverrides: {
          columnHeaders: {
            backgroundColor: 'white !important',
            color: 'black !important',
            fontWeight: 'bold !important',
            borderBottom: '2px solid #dee2e6 !important',
          },
        }
      }
    }
  });

  // Cargar todos los hitos (sin filtros) una única vez al montar
  useEffect(() => {
    cargarTodos();
  }, []);

  const cargarTodos = async () => {
    setLoading(true);
    try {
      const ESTADOS_OPERACIONES = ['aprobado', 'planificada', 'en_curso', 'bloqueada', 'cancelada', 'finalizada'];
      const [hitosRes, proyectosRes] = await Promise.all([
        hitosFacturacionAPI.getAll({ page_size: 10000 }),
        iniciativasAPI.getProyectosOperacionesDropdown()
      ]);
      const todos = hitosRes.data.results || hitosRes.data || [];
      setTodosLosHitos(todos);

      // Opciones para los dropdowns
      const mesesNombres = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
      const mesesSet = new Set();
      const añosSet = new Set();
      todos.forEach(h => {
        if (h.fecha) {
          const d = new Date(h.fecha);
          mesesSet.add(d.getMonth() + 1);
          añosSet.add(d.getFullYear());
        }
      });
      const mesesUnicos = [...mesesSet].sort((a, b) => a - b).map(m => ({ value: String(m), label: mesesNombres[m - 1] }));
      const añosUnicos = [...añosSet].sort((a, b) => b - a).map(y => ({ value: String(y), label: String(y) }));
      setMeses(mesesUnicos);
      setAños(añosUnicos);

      // Cargar todos los proyectos de Operaciones desde el endpoint ligero
      const todosLosProyectos = proyectosRes.data || [];
      const proyectosUnicos = Array.from(
        new Map(todosLosProyectos.map(p => [p.id, {
          id: p.id,
          nombre: p.nombre_de_la_oportunidad,
          nombre_de_la_oportunidad: p.nombre_de_la_oportunidad,
          importe_total_cliente: p.importe_total_cliente,
          nombre_sociedad_comercial: p.nombre_sociedad_comercial || '',
          nombre_sociedad_final: p.nombre_sociedad_final || ''
        }])).values()
      );
      setIniciativas(proyectosUnicos);

      const clientesUnicos = [...new Set(todos.map(h => h.cliente).filter(c => c))].sort();
      setClientes(clientesUnicos);

      const sociedadesUnicas = [...new Set(todos.map(h => h.nombre_sociedad_comercial).filter(s => s))].sort();
      setSociedades(sociedadesUnicas);

      setError(null);
    } catch (err) {
      console.error('Error cargando hitos:', err);
      setError('Error al cargar los hitos de facturación');
    } finally {
      setLoading(false);
    }
  };

  // Filtrado client-side
  const hitosFiltrados = useMemo(() => {
    let r = todosLosHitos;

    if (filtros.mesFiltro.includes('__NONE__')) return [];
    if (filtros.mesFiltro.length > 0) {
      r = r.filter(h => {
        if (!h.fecha) return false;
        const d = new Date(h.fecha);
        return filtros.mesFiltro.includes(String(d.getMonth() + 1));
      });
    }

    if (filtros.añoFiltro.includes('__NONE__')) return [];
    if (filtros.añoFiltro.length > 0) {
      r = r.filter(h => {
        if (!h.fecha) return false;
        const d = new Date(h.fecha);
        return filtros.añoFiltro.includes(String(d.getFullYear()));
      });
    }

    if (filtros.searchText) {
      const s = filtros.searchText.toLowerCase();
      r = r.filter(h =>
        (h.nombre && h.nombre.toLowerCase().includes(s)) ||
        (h.iniciativa_nombre && h.iniciativa_nombre.toLowerCase().includes(s))
      );
    }

    if (filtros.iniciativasFiltro.includes('__NONE__')) return [];
    if (filtros.iniciativasFiltro.length > 0) {
      r = r.filter(h => filtros.iniciativasFiltro.includes(h.iniciativa));
    }

    if (filtros.estadoFiltro.includes('__NONE__')) return [];
    if (filtros.estadoFiltro.length > 0) {
      r = r.filter(h => {
        if (filtros.estadoFiltro.includes('facturado') && h.facturado) return true;
        if (filtros.estadoFiltro.includes('pendiente') && !h.facturado) return true;
        return false;
      });
    }

    if (filtros.clientesFiltro.includes('__NONE__')) return [];
    if (filtros.clientesFiltro.length > 0) {
      r = r.filter(h => filtros.clientesFiltro.includes(h.cliente));
    }

    if (filtros.sociedadesFiltro.includes('__NONE__')) return [];
    if (filtros.sociedadesFiltro.length > 0) {
      r = r.filter(h => filtros.sociedadesFiltro.includes(h.nombre_sociedad_comercial));
    }

    return r;
  }, [todosLosHitos, filtros]);

  // KPIs calculados client-side a partir de los hitos filtrados
  const kpis = useMemo(() => {
    const hoy = new Date();
    const mesActual = hoy.getMonth() + 1;
    const añoActual = hoy.getFullYear();
    const añoLabel = filtros.añoFiltro.length === 1 && !filtros.añoFiltro.includes('__NONE__') ? Number(filtros.añoFiltro[0]) : añoActual;

    const sumar = (arr) => arr.reduce((sum, h) => sum + (parseFloat(h.importe) || 0), 0);

    // 1. Facturable: suma total del importe de los hitos filtrados
    const facturable_año = sumar(hitosFiltrados);

    // 2. Facturado acumulado: facturado=true cuya fecha de referencia es anterior al mes en curso del año actual.
    //    Si el año de la fecha es anterior al año actual, todos sus meses ya han pasado → contar siempre.
    //    Si el año es el actual, contar solo si el mes es anterior al mes actual.
    //    Años futuros nunca cuentan.
    const facturado_acumulado = sumar(
      hitosFiltrados.filter(h => {
        if (!h.facturado) return false;
        const fechaRef = h.fecha_facturacion
          ? new Date(h.fecha_facturacion)
          : (h.fecha ? new Date(h.fecha) : null);
        if (!fechaRef) return false;
        const mes = fechaRef.getMonth() + 1;
        const año = fechaRef.getFullYear();
        return año < añoActual || (año === añoActual && mes < mesActual);
      })
    );

    // 3. Facturación mes en curso: hitos facturados cuya fecha_facturacion cae en el mes y año actual
    const facturacion_mes_actual = sumar(
      hitosFiltrados.filter(h => {
        if (!h.facturado || !h.fecha_facturacion) return false;
        const f = new Date(h.fecha_facturacion);
        return f.getFullYear() === añoActual && (f.getMonth() + 1) === mesActual;
      })
    );

    // 4. Pendiente de facturar: hitos no facturados
    const pendiente_facturar = sumar(hitosFiltrados.filter(h => !h.facturado));

    return { facturable_año, facturado_acumulado, facturacion_mes_actual, pendiente_facturar, año: añoLabel, mes: mesActual };
  }, [hitosFiltrados, filtros.añosFiltro]);

  // Helpers multi-select (mismo patrón que Operaciones)
  const isAllSelected = (selectedItems, allItems) =>
    selectedItems.length === 0 || (allItems.length > 0 && selectedItems.length === allItems.length);

  const handleSelectChange = (event, allItems, campo, currentSelection) => {
    const value = event.target.value;
    let newVal;
    if (value[value.length - 1] === 'all') {
      newVal = isAllSelected(currentSelection, allItems) ? ['__NONE__'] : [];
    } else {
      newVal = value.filter(v => v !== '__NONE__');
    }
    handleFiltroChange(campo, newVal);
  };

  const getRenderValue = (selectedItems, label) => {
    if (selectedItems.includes('__NONE__')) return 'Ninguno';
    if (selectedItems.length === 0) return 'Todos';
    if (selectedItems.length === 1) {
      if (label === 'iniciativas') {
        const ini = iniciativas.find(i => i.id === selectedItems[0]);
        return ini ? ini.nombre : selectedItems[0];
      }
      if (label === 'estado') {
        return selectedItems[0] === 'facturado' ? 'Facturado' : 'Pendiente';
      }
      if (label === 'meses') {
        const mes = meses.find(m => m.value === selectedItems[0]);
        return mes ? mes.label : String(selectedItems[0]);
      }
      if (label === 'años') {
        return String(selectedItems[0]);
      }
      return String(selectedItems[0]);
    }
    return `${selectedItems.length} seleccionado${selectedItems.length > 1 ? 's' : ''}`;
  };

  const handleFiltroChange = (campo, valor) => {
    setFiltros(prev => ({ ...prev, [campo]: valor }));
  };

  const handleColumnVisibilityModelChange = (newModel) => {
    setColumnVisibilityModel(newModel);
    localStorage.setItem('hitosFacturacion_columnVisibility', JSON.stringify(newModel));
  };

  const resetColumnVisibility = () => {
    setColumnVisibilityModel({});
    localStorage.removeItem('hitosFacturacion_columnVisibility');
  };

  const formatCurrency = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    const num = Number(value);
    if (isNaN(num)) return '-';
    return new Intl.NumberFormat(undefined, {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
      useGrouping: true,
    }).format(num);
  };

  const formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-ES');
  };

  const handleAbrirModal = (hito = null) => {
    setHitoSeleccionado(hito);
    setModalOpen(true);
  };

  const handleCerrarModal = () => {
    setModalOpen(false);
    setHitoSeleccionado(null);
  };

  const handleGuardarHito = async () => {
    await cargarTodos();
    handleCerrarModal();
  };



  // Configuración de columnas
  const columns = [
    {
      field: 'numero_hito',
      headerName: 'Nº Hito',
      width: 70,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
    },
    {
      field: 'iniciativa_nombre',
      headerName: 'Proyecto',
      flex: 1.5,
      minWidth: 250,
      renderCell: (params) => (
        <Link
          to={`/projects/${params.row.iniciativa}?from=hitos_facturacion`}
          style={{
            textDecoration: 'none',
            color: '#0d6efd',
            fontWeight: 'bold'
          }}
        >
          {params.value || '-'}
        </Link>
      )
    },
    {
      field: 'nombre',
      headerName: 'Nombre del Hito',
      flex: 1.2,
      minWidth: 200,
    },
    {
      field: 'fecha',
      headerName: 'Fecha Prevista',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      type: 'date',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'porcentaje',
      headerName: '% Hito',
      width: 100,
      align: 'center',
      headerAlign: 'center',
      type: 'number',
      renderCell: (params) => params.value ? `${params.value}%` : '-',
    },
    {
      field: 'importe',
      headerName: 'Importe',
      width: 130,
      align: 'right',
      headerAlign: 'center',
      renderCell: (params) => formatCurrency(params.value),
    },
    {
      field: 'cliente',
      headerName: 'Cliente',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'facturado',
      headerName: 'Estado',
      width: 130,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => (
        <Chip
          label={params.value ? 'Facturado' : 'Pendiente'}
          color={params.value ? 'success' : 'default'}
          variant={params.value ? 'filled' : 'outlined'}
          size="small"
        />
      )
    },
    {
      field: 'fecha_facturacion',
      headerName: 'Fecha Facturación',
      width: 140,
      align: 'center',
      headerAlign: 'center',
      type: 'date',
      valueGetter: (value) => value ? new Date(value) : null,
      renderCell: (params) => formatDate(params.value),
    },
    {
      field: 'area_ejecucion_nombre',
      headerName: 'Área Ejecución',
      width: 150,
      align: 'center',
      headerAlign: 'center',
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'sociedad_final',
      headerName: 'Sociedad Final',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'sociedad_cabecera',
      headerName: 'Sociedad Cabecera',
      flex: 1,
      minWidth: 180,
      renderCell: (params) => params.value || '-',
    },
    {
      field: 'zona_geografica',
      headerName: 'Zona Geográfica',
      flex: 1,
      minWidth: 150,
      renderCell: (params) => params.row.zona_geografica_display || params.value || '-',
    },
    {
      field: 'pais',
      headerName: 'País',
      flex: 1,
      minWidth: 140,
      renderCell: (params) => params.row.pais_display || params.value || '-',
    },
    {
      field: 'observaciones',
      headerName: 'Observaciones',
      flex: 1,
      minWidth: 200,
      renderCell: (params) => (
        <Tooltip title={params.value || ''} arrow>
          <span style={{
            overflow: 'hidden',
            textOverflow: 'ellipsis',
            whiteSpace: 'nowrap',
            display: 'block'
          }}>
            {params.value || '-'}
          </span>
        </Tooltip>
      ),
    },
    {
      field: 'acciones',
      headerName: 'Acciones',
      width: 120,
      align: 'center',
      headerAlign: 'center',
      sortable: false,
      filterable: false,
      renderCell: (params) => (
        <Box>
          <Tooltip title="Editar">
            <IconButton
              size="small"
              color="primary"
              onClick={() => handleAbrirModal(params.row)}
            >
              <EditIcon fontSize="small" />
            </IconButton>
          </Tooltip>

        </Box>
      ),
    }
  ];

  const normalizeNumericValue = (value) => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'number') return Number.isFinite(value) ? value : null;

    const raw = String(value).trim();
    if (!raw) return null;

    let cleaned = raw
      .replace(/\u00a0/g, '')
      .replace(/€/g, '')
      .replace(/%/g, '')
      .replace(/[hH]\b/g, '')
      .replace(/horas?/gi, '')
      .replace(/\s+/g, '');

    if (!/[0-9]/.test(cleaned)) return null;

    if (cleaned.includes(',') && cleaned.includes('.')) {
      cleaned = cleaned.replace(/\./g, '').replace(',', '.');
    } else if (cleaned.includes(',')) {
      cleaned = cleaned.replace(',', '.');
    }

    const num = Number(cleaned);
    return Number.isFinite(num) ? num : null;
  };

  const isColumnVisible = (field) => columnVisibilityModel[field] !== false;

  const getExportableColumns = () => {
    return columns.filter((column) => column.field && column.field !== 'acciones' && isColumnVisible(column.field));
  };

  const getExportCellValue = (row, field) => {
    switch (field) {
      case 'numero_hito':
        return normalizeNumericValue(row.numero_hito);
      case 'iniciativa_nombre':
        return row.iniciativa_nombre || '';
      case 'nombre':
        return row.nombre || '';
      case 'fecha':
        return row.fecha || '';
      case 'porcentaje': {
        const value = normalizeNumericValue(row.porcentaje);
        return value === null ? null : value / 100;
      }
      case 'importe':
        return normalizeNumericValue(row.importe);
      case 'cliente':
        return row.cliente || '';
      case 'facturado':
        return row.facturado ? 'Facturado' : 'Pendiente';
      case 'fecha_facturacion':
        return row.fecha_facturacion || '';
      case 'area_ejecucion_nombre':
        return row.area_ejecucion_nombre || '';
      case 'sociedad_final':
        return row.sociedad_final || '';
      case 'sociedad_cabecera':
        return row.sociedad_cabecera || '';
      case 'zona_geografica':
        return row.zona_geografica_display || row.zona_geografica || '';
      case 'pais':
        return row.pais_display || row.pais || '';
      case 'observaciones':
        return row.observaciones || '';
      default:
        return row[field] ?? '';
    }
  };

  if (loading && todosLosHitos.length === 0) {
    return (
      <div className="container mt-4">
        <div className="text-center">
          <div className="spinner-border text-primary" role="status">
            <span className="visually-hidden">Cargando...</span>
          </div>
          <p className="mt-2">Cargando hitos de facturación...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="container mt-4">
        <div className="alert alert-danger" role="alert">
          {error}
        </div>
      </div>
    );
  }

  const handleLimpiarFiltros = () => {
    setFiltros({
      mesFiltro: [],
      añoFiltro: [],
      iniciativasFiltro: [],
      estadoFiltro: [],
      clientesFiltro: [],
      sociedadesFiltro: [],
      searchText: ''
    });
  };

  return (
    <ThemeProvider theme={theme}>
      <Box sx={{
        width: '100%',
        maxWidth: '100vw',
        overflow: 'hidden',
        px: { xs: 1, sm: 2, md: 3 },
        py: 2
      }}>

        {/* Tarjeta superior: KPIs + Filtros */}
        <Card sx={{ mb: 2 }}>
          <CardContent sx={{ pb: 0 }}>

            {/* KPIs Dashboard */}
            <Box sx={{
              mb: 2,
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: 1.5
            }}>
              {/* Facturable año */}
              <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#0d6efd', mb: 0.5 }}>
                  {formatCurrency(kpis.facturable_año)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Facturable {filtros.añoFiltro.length === 1 && !filtros.añoFiltro.includes('__NONE__') ? filtros.añoFiltro[0] : 'Todos'}
                </Typography>
              </Paper>

              {/* Facturado acumulado hasta mes anterior */}
              <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#198754', mb: 0.5 }}>
                  {formatCurrency(kpis.facturado_acumulado)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Facturado (hasta mes anterior)
                </Typography>
              </Paper>

              {/* Facturación mes en curso */}
              <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#6f42c1', mb: 0.5 }}>
                  {formatCurrency(kpis.facturacion_mes_actual)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Facturación mes en curso
                </Typography>
              </Paper>

              {/* Pendiente de facturar */}
              <Paper sx={{ p: 1.5, textAlign: 'center', backgroundColor: '#f8f9fa', border: '1px solid #dee2e6', borderRadius: '8px' }}>
                <Typography variant="h6" sx={{ fontSize: '1.2rem', fontWeight: 'bold', color: '#ffc107', mb: 0.5 }}>
                  {formatCurrency(kpis.pendiente_facturar)}
                </Typography>
                <Typography variant="caption" sx={{ fontSize: '0.75rem', color: '#6c757d', lineHeight: 1.2 }}>
                  Pte. Facturar
                </Typography>
              </Paper>
            </Box>

            {/* Filtros */}
            <Box sx={{ mt: '2rem', mb: '1rem', display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center' }}>

              {/* Búsqueda libre: nombre hito + proyecto */}
              <TextField
                variant="outlined"
                placeholder="Buscar por proyecto o nombre del hito..."
                value={filtros.searchText}
                onChange={(e) => handleFiltroChange('searchText', e.target.value)}
                size="small"
                sx={{ minWidth: 260, flex: 1, maxWidth: { xs: '100%', md: 320 } }}
              />

              {/* Mes */}
              <FormControl size="small" sx={{ minWidth: 130 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Mes</InputLabel>
                <Select
                  multiple
                  value={filtros.mesFiltro}
                  label="Mes"
                  onChange={(e) => handleSelectChange(e, meses.map(m => m.value), 'mesFiltro', filtros.mesFiltro)}
                  renderValue={() => getRenderValue(filtros.mesFiltro, 'meses')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(filtros.mesFiltro, meses.map(m => m.value))}
                        indeterminate={filtros.mesFiltro.length > 0 && !filtros.mesFiltro.includes('__NONE__') && !isAllSelected(filtros.mesFiltro, meses.map(m => m.value))}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {meses.map(m => (
                    <MenuItem key={m.value} value={m.value} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={filtros.mesFiltro.length === 0 || filtros.mesFiltro.includes(m.value)} />
                      </ListItemIcon>
                      <ListItemText primary={m.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Año */}
              <FormControl size="small" sx={{ minWidth: 100 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Año</InputLabel>
                <Select
                  multiple
                  value={filtros.añoFiltro}
                  label="Año"
                  onChange={(e) => handleSelectChange(e, años.map(a => a.value), 'añoFiltro', filtros.añoFiltro)}
                  renderValue={() => getRenderValue(filtros.añoFiltro, 'años')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(filtros.añoFiltro, años.map(a => a.value))}
                        indeterminate={filtros.añoFiltro.length > 0 && !filtros.añoFiltro.includes('__NONE__') && !isAllSelected(filtros.añoFiltro, años.map(a => a.value))}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {años.map(a => (
                    <MenuItem key={a.value} value={a.value} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={filtros.añoFiltro.length === 0 || filtros.añoFiltro.includes(a.value)} />
                      </ListItemIcon>
                      <ListItemText primary={a.label} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Proyecto */}
              <FormControl size="small" sx={{ minWidth: 220 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Proyecto</InputLabel>
                <Select
                  multiple
                  value={filtros.iniciativasFiltro}
                  label="Proyecto"
                  onChange={(e) => handleSelectChange(e, iniciativas.map(i => i.id), 'iniciativasFiltro', filtros.iniciativasFiltro)}
                  renderValue={() => getRenderValue(filtros.iniciativasFiltro, 'iniciativas')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(filtros.iniciativasFiltro, iniciativas.map(i => i.id))}
                        indeterminate={filtros.iniciativasFiltro.length > 0 && !filtros.iniciativasFiltro.includes('__NONE__') && !isAllSelected(filtros.iniciativasFiltro, iniciativas.map(i => i.id))}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {iniciativas.map(ini => (
                    <MenuItem key={ini.id} value={ini.id} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={filtros.iniciativasFiltro.length === 0 || filtros.iniciativasFiltro.includes(ini.id)} />
                      </ListItemIcon>
                      <ListItemText primary={ini.nombre} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* Estado */}
              <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Estado</InputLabel>
                <Select
                  multiple
                  value={filtros.estadoFiltro}
                  label="Estado"
                  onChange={(e) => handleSelectChange(e, ['facturado', 'pendiente'], 'estadoFiltro', filtros.estadoFiltro)}
                  renderValue={() => getRenderValue(filtros.estadoFiltro, 'estado')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(filtros.estadoFiltro, ['facturado', 'pendiente'])}
                        indeterminate={filtros.estadoFiltro.length === 1 && !filtros.estadoFiltro.includes('__NONE__')}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  <MenuItem value="facturado" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={filtros.estadoFiltro.length === 0 || filtros.estadoFiltro.includes('facturado')} />
                    </ListItemIcon>
                    <ListItemText primary="Facturado" />
                  </MenuItem>
                  <MenuItem value="pendiente" sx={{ fontSize: '0.85rem' }}>
                    <ListItemIcon>
                      <Checkbox checked={filtros.estadoFiltro.length === 0 || filtros.estadoFiltro.includes('pendiente')} />
                    </ListItemIcon>
                    <ListItemText primary="Pendiente" />
                  </MenuItem>
                </Select>
              </FormControl>

              {/* Cliente */}
              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Cliente</InputLabel>
                <Select
                  multiple
                  value={filtros.clientesFiltro}
                  label="Cliente"
                  onChange={(e) => handleSelectChange(e, clientes, 'clientesFiltro', filtros.clientesFiltro)}
                  renderValue={() => getRenderValue(filtros.clientesFiltro, 'clientes')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(filtros.clientesFiltro, clientes)}
                        indeterminate={filtros.clientesFiltro.length > 0 && !filtros.clientesFiltro.includes('__NONE__') && !isAllSelected(filtros.clientesFiltro, clientes)}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {[...clientes].sort((a, b) => a.localeCompare(b, 'es', { sensitivity: 'base' })).map(c => (
                    <MenuItem key={c} value={c} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={filtros.clientesFiltro.length === 0 || filtros.clientesFiltro.includes(c)} />
                      </ListItemIcon>
                      <ListItemText primary={c} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              {/* <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Sociedad Cabecera</InputLabel>
                <Select
                  multiple
                  value={filtros.sociedadesFiltro}
                  label="Sociedad Cabecera"
                  onChange={(e) => handleSelectChange(e, sociedades, 'sociedadesFiltro', filtros.sociedadesFiltro)}
                  renderValue={() => getRenderValue(filtros.sociedadesFiltro, 'sociedades')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(filtros.sociedadesFiltro, sociedades)}
                        indeterminate={filtros.sociedadesFiltro.length > 0 && !filtros.sociedadesFiltro.includes('__NONE__') && !isAllSelected(filtros.sociedadesFiltro, sociedades)}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {sociedades.map(s => (
                    <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={filtros.sociedadesFiltro.length === 0 || filtros.sociedadesFiltro.includes(s)} />
                      </ListItemIcon>
                      <ListItemText primary={s} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>

              <FormControl size="small" sx={{ minWidth: 200 }}>
                <InputLabel sx={{ fontSize: '0.85rem' }} shrink>Sociedad Final (PENDIENTE)</InputLabel>
                <Select
                  multiple
                  value={filtros.sociedadesFiltro}
                  label="Sociedad Final (PENDIENTE)"
                  onChange={(e) => handleSelectChange(e, sociedades, 'sociedadesFiltro', filtros.sociedadesFiltro)}
                  renderValue={() => getRenderValue(filtros.sociedadesFiltro, 'sociedades')}
                  sx={{ fontSize: '0.85rem' }}
                  displayEmpty
                >
                  <MenuItem value="all">
                    <ListItemIcon>
                      <Checkbox
                        checked={isAllSelected(filtros.sociedadesFiltro, sociedades)}
                        indeterminate={filtros.sociedadesFiltro.length > 0 && !filtros.sociedadesFiltro.includes('__NONE__') && !isAllSelected(filtros.sociedadesFiltro, sociedades)}
                      />
                    </ListItemIcon>
                    <ListItemText primary="Seleccionar todos" />
                  </MenuItem>
                  {sociedades.map(s => (
                    <MenuItem key={s} value={s} sx={{ fontSize: '0.85rem' }}>
                      <ListItemIcon>
                        <Checkbox checked={filtros.sociedadesFiltro.length === 0 || filtros.sociedadesFiltro.includes(s)} />
                      </ListItemIcon>
                      <ListItemText primary={s} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl> */}

              {/* Botón Limpiar filtros */}
              <Button
                variant="contained"
                size="small"
                onClick={handleLimpiarFiltros}
                sx={{
                  backgroundColor: '#dc3545',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(220, 53, 69, 0.2)',
                  '&:hover': {
                    backgroundColor: '#c82333',
                    boxShadow: '0 4px 8px rgba(220, 53, 69, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <i className="fa-solid fa-filter-circle-xmark" style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                Limpiar filtros
              </Button>

              {/* Botón Actualizar */}
              <Button
                variant="contained"
                size="small"
                onClick={cargarTodos}
                disabled={loading}
                sx={{
                  backgroundColor: '#007bff',
                  color: 'white',
                  fontSize: '0.8rem',
                  fontWeight: '500',
                  px: 2.5,
                  py: 1,
                  textTransform: 'none',
                  borderRadius: '6px',
                  boxShadow: '0 2px 4px rgba(0, 123, 255, 0.2)',
                  '&:hover': {
                    backgroundColor: '#0056b3',
                    boxShadow: '0 4px 8px rgba(0, 123, 255, 0.3)',
                    transform: 'translateY(-1px)'
                  },
                  '&:active': {
                    transform: 'translateY(0px)'
                  },
                  '&:disabled': {
                    backgroundColor: '#e9ecef',
                    color: '#6c757d'
                  },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                <i className={`fa-solid ${loading ? 'fa-spinner fa-spin' : 'fa-arrows-rotate'}`} style={{ marginRight: '6px', fontSize: '0.9rem' }}></i>
                {loading ? 'Cargando...' : 'Actualizar'}
              </Button>

            </Box>
          </CardContent>
        </Card>

        {/* Tarjeta de tabla */}
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
              <Button
                variant="outlined"
                size="small"
                onClick={() => handleAbrirModal()}
                sx={{
                  backgroundColor: '#212529',
                  color: '#fff',
                  borderColor: '#212529',
                  fontSize: '0.8rem',
                  fontWeight: 'bold',
                  px: 2,
                  py: 1,
                  textTransform: 'none',
                  boxShadow: '0 2px 6px rgba(0,0,0,0.15)',
                  '&:hover': {
                    backgroundColor: '#495057',
                    borderColor: '#212529',
                    color: '#fff',
                    boxShadow: '0 4px 12px rgba(0,0,0,0.25)',
                    transform: 'translateY(-1px)',
                  },
                  whiteSpace: 'nowrap',
                  transition: 'all 0.2s ease-in-out'
                }}
              >
                + Agregar Hito
              </Button>
            </Box>

            <Box sx={{ height: 'calc(100vh - 460px)', minHeight: '400px', width: '100%', position: 'relative' }}>
              <DataGrid
                ref={gridRef}
                rows={hitosFiltrados}
                columns={columns}
                pageSize={25}
                rowsPerPageOptions={[10, 25, 50, 100]}
                disableSelectionOnClick
                initialState={{
                  sorting: {
                    sortModel: [{ field: 'fecha_facturacion', sort: 'desc' }],
                  },
                }}
                columnVisibilityModel={columnVisibilityModel}
                onColumnVisibilityModelChange={handleColumnVisibilityModelChange}
                loading={loading}
                sx={{
                  '& .MuiDataGrid-cell': { padding: '8px' },
                  '& .MuiDataGrid-columnHeaders': {
                    backgroundColor: 'white',
                    color: 'black',
                    fontWeight: 'bold',
                  }
                }}
              />

              {/* Botón Restablecer columnas */}
              <Button
                onClick={resetColumnVisibility}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 140,
                  minWidth: 'auto',
                  width: 'auto',
                  height: 32,
                  padding: '0 8px',
                  color: '#6c757d',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#495057'
                  }
                }}
              >
                Restablecer columnas
              </Button>

              {/* Botón Exportar tabla */}
              <Button
                onClick={() => {
                  const exportColumns = getExportableColumns();

                  const headerRow = exportColumns.map((column) => {
                    if (typeof column.headerName === 'string' && column.headerName.trim()) {
                      return column.headerName;
                    }
                    return column.field;
                  });

                  const filas = [
                    headerRow,
                    ...hitosFiltrados.map((row) =>
                      exportColumns.map((column) => getExportCellValue(row, column.field))
                    )
                  ];

                  const ws = XLSX.utils.aoa_to_sheet(filas);
                  // Aplicar negrita a la cabecera (primera fila)
                  const numCols = filas[0].length;
                  for (let c = 0; c < numCols; c++) {
                    const cellRef = XLSX.utils.encode_cell({ r: 0, c });
                    if (ws[cellRef]) {
                      ws[cellRef].s = { font: { bold: true } };
                    }
                  }
                  const wb = XLSX.utils.book_new();
                  XLSX.utils.book_append_sheet(wb, ws, 'Detalle de facturación');
                  const now = new Date();
                  const yyyymm = `${now.getFullYear()}${String(now.getMonth() + 1).padStart(2, '0')}`;
                  XLSX.writeFile(wb, `ATS ${yyyymm}_ Detalle Servicios_DigitalHub.xlsx`, { bookType: 'xlsx', cellStyles: true });
                }}
                sx={{
                  position: 'absolute',
                  bottom: 8,
                  left: 16,
                  minWidth: 'auto',
                  width: 'auto',
                  height: 32,
                  padding: '0 8px',
                  color: '#6c757d',
                  zIndex: 10,
                  backgroundColor: 'rgba(255, 255, 255, 0.9)',
                  borderRadius: '16px',
                  fontSize: '0.75rem',
                  textTransform: 'none',
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                    color: '#495057'
                  }
                }}
              >
                Exportar tabla
                <DownloadIcon sx={{ fontSize: 16, ml: 0.5 }} />
              </Button>
            </Box>
          </CardContent>
        </Card>

        {/* Modal de edición/creación */}
        <HitoModal
          open={modalOpen}
          onClose={handleCerrarModal}
          hito={hitoSeleccionado}
          onSave={handleGuardarHito}
          iniciativas={iniciativas}
        />
      </Box>
    </ThemeProvider>
  );
};

export default TablaHitosFacturacion;
